import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Dropzone from 'react-dropzone';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import classnames from 'classnames';
import Button from 'react-bootstrap/Button';
import MuiButton from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import ProgressBar from 'react-bootstrap/ProgressBar';
import T from 'i18n';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt, faFile } from '@fortawesome/free-solid-svg-icons';
import { Prompt } from 'react-router';
import { Modal } from 'features/common/MaterialBasedComponents';
import history from 'common/history';
import { confidentialityRingCategoryLabel } from 'features/case/enums';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import {
  cancelUploadFiles,
  closeUploadFilesProgress,
  dismissUploadFilesFeedback,
  uploadFiles,
} from 'features/case/redux/uploadFiles';
import _ from 'lodash';
import {
  selectCurrentFolder,
  selectDocuments,
  selectQuickUploadLimit,
  selectQuickUploadFileSizeLimit,
} from 'common/selectors';
import { Modal as MuiModal } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

const processError = (error: any) => {
  switch (error.status) {
    default:
      if (typeof error.body === 'object')
        return error.body.body || error.body.statusText || error.body.message || '';
      else return error.body || error.statusText || '';
  }
};

const FileProgress = ({ id, name, progress, error }: any) => {
  return (
    <div className="file-container">
      <FontAwesomeIcon icon={faFile} />
      <div className="progress-container">
        <div className="title-container">
          <span>
            {[name, id || (error && error.body && error.body.fileId)].filter(Boolean).join(' - ')}
          </span>
          {error && processError(error)}
        </div>
        <ProgressBar
          variant={!error ? 'success' : error.status === 409 ? 'warning' : 'danger'}
          now={progress}
          label={progress ? `${progress.split('.')[0]}%` : ''}
        />
      </div>
    </div>
  );
};

const FileStatusRender = ({
  filesToUpload,
  loading,
}: {
  filesToUpload?: any[];
  loading?: boolean;
}) => {
  const currentFolder = useSelector(selectCurrentFolder) as any;
  const documents = useSelector(selectDocuments) as any[];

  const exportCSV = () => {
    const failedFiles = _.filter(filesToUpload, (file) => file?.error && file?.error?.body);

    const csvHeader = 'File Name, Error Message, Name on Trialview, Trialview ID\n';

    const csvRows = failedFiles
      .map((file) => {
        const fileName = file.name;
        const errorMessage = file.error.body.body;

        const duplicateMatch = errorMessage.match(/duplicate of (\w+-\d+-\d+)/);
        let nameOnTrialview = '';
        let trialviewId = '';

        if (duplicateMatch) {
          trialviewId = duplicateMatch[1];
          const matchingDocument = documents.find((doc) => doc.id === trialviewId);
          if (matchingDocument) {
            nameOnTrialview = matchingDocument.name;
          }
        }
        return `"${fileName}","${errorMessage}","${nameOnTrialview}","${trialviewId}"`;
      })
      .join('\n');

    const csvContent = csvHeader + csvRows;

    const bom = '\uFEFF'; // Byte Order Mark for Microsoft Excel parsing, not necessary for other csv opening apps.
    const blob = new Blob([bom + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const timeStamp = `${new Date().getFullYear()}-${new Date().getDate()}-${
      new Date().getMonth() + 1
    }--${new Date().getHours()}-${new Date().getMinutes()}-${new Date().getSeconds()}`;
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${currentFolder?.name}_Failed_Uploads_${timeStamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [failed, setFailed] = useState(0);
  const [successful, setSuccessful] = useState(0);
  const [duplicates, setDuplicates] = useState(0);
  const [oversized, setOversized] = useState(0);

  const getCounts = () => {
    const duplicateFiles = _.filter(filesToUpload, (file) => file?.error?.status === 409).length;
    const oversizedFiles = _.filter(filesToUpload, (file) => file?.error?.status === 413).length;
    const failedFiles = _.filter(
      filesToUpload,
      (file) =>
        file?.error &&
        file?.error?.body &&
        file?.error?.status !== 409 &&
        file?.error?.status !== 413,
    ).length;
    const successfulFiles = _.filter(
      filesToUpload,
      (file) => !file?.error?.status && !file?.error?.body,
    ).length;

    setFailed(failedFiles);
    setSuccessful(successfulFiles);
    setDuplicates(duplicateFiles);
    setOversized(oversizedFiles);
  };

  useEffect(() => {
    getCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesToUpload]);

  return (
    <div className="file-stats-row">
      {filesToUpload?.length && !loading ? (
        <div>
          {T.translate('generic.exportedStatuses.loading')}: {filesToUpload?.length}
        </div>
      ) : (
        <>
          <div>
            {' '}
            {T.translate('generic.exportedStatuses.total')}: {filesToUpload?.length}
            <span>
              {' '}
              {T.translate('generic.exportedStatuses.success')}: {successful}
            </span>
            {failed > 0 && (
              <span>
                {' '}
                {T.translate('generic.exportedStatuses.failed')}: {failed}
              </span>
            )}
            {duplicates > 0 && (
              <span>
                {' '}
                {T.translate('generic.exportedStatuses.duplicates')}: {duplicates}
              </span>
            )}
            {oversized > 0 && (
              <span>
                {' '}
                {T.translate('generic.exportedStatuses.oversized')}: {oversized}
              </span>
            )}
          </div>
          {_.filter(filesToUpload, (file) => file?.error && file?.error?.body).length > 0 && (
            <MuiButton
              variant="text"
              sx={{ paddingLeft: 0, justifyContent: 'start' }}
              size="small"
              onClick={() => {
                exportCSV();
              }}
            >
              {T.translate('generic.exportFailedFiles')}
            </MuiButton>
          )}
        </>
      )}
    </div>
  );
};

type FileDropZoneProps = {
  folderId?: string;
  noInput?: boolean;
  showProgress?: boolean;
  showInfo?: boolean;
  confidentialityRing?: string;
  children: any;
  noDrop?: boolean;
  refLink?: any;
  onDropCustom?: (files: File[], context?: { batchId: string; uploadInnerFile?: boolean; folderId?: string }) => void;
  onDragEnter?: any;
  uploadInnerFile?: any;
  setUploadInnerFile?: any;
  showSidebar?: boolean;
};

export default ({
  folderId,
  noInput,
  showProgress,
  showInfo,
  confidentialityRing,
  children,
  noDrop,
  refLink,
  onDropCustom,
  onDragEnter,
  uploadInnerFile,
  setUploadInnerFile,
  showSidebar,
}: FileDropZoneProps) => {
  const quickUploadLimitRaw = useSelector(selectQuickUploadLimit) as number;
  const quickUploadLimit =
    typeof quickUploadLimitRaw === 'number' && !Number.isNaN(quickUploadLimitRaw)
      ? quickUploadLimitRaw
      : Number(quickUploadLimitRaw) || 50;

  const quickUploadFileSizeLimitRaw = useSelector(selectQuickUploadFileSizeLimit) as number;
  const quickUploadFileSizeLimit =
    typeof quickUploadFileSizeLimitRaw === 'number' && !Number.isNaN(quickUploadFileSizeLimitRaw)
      ? quickUploadFileSizeLimitRaw
      : Number(quickUploadFileSizeLimitRaw) || 100;

  const quickUploadFileSizeLimitBytes = quickUploadFileSizeLimit * 1024 * 1024;

  const defaultLimitMessage = useMemo(() => {
    return T.translate('generic.quickUploadLimitsExceeded', {
      details: `• Max ${quickUploadLimit} files\n• Max ${quickUploadFileSizeLimit}MB per file`,
    });
  }, [quickUploadLimit, quickUploadFileSizeLimit]);
  const [modalVisible, setModalVisible] = useState(false);
  const [lastLocation, setLastLocation] = useState({ pathname: '' });
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>(defaultLimitMessage);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  useEffect(() => {
    setAlertMessage(defaultLimitMessage);
  }, [defaultLimitMessage]);
  // Function to validate files against upload limits
  const validateFiles = useCallback(
    (files: File[]) => {
      const errors: string[] = [];

      // Check file count limit - this blocks upload
      if (quickUploadLimit > 0 && files.length > quickUploadLimit) {
        errors.push(`• Too many files: ${files.length} files (max ${quickUploadLimit})`);
      }

      // Note: We don't block oversized files here anymore - they'll be handled in the backend
      // and shown in the upload list with warning status

      return {
        isValid: errors.length === 0,
        errorMessage:
          errors.length > 0
            ? T.translate('generic.quickUploadLimitsExceeded', { details: errors.join('\n') })
            : '',
      };
    },
    [quickUploadLimit],
  );

  // Function to show the custom alert
  const showCustomAlert = useCallback(
    (message?: string) => {
      setAlertMessage((message as string) || defaultLimitMessage);
      setAlertVisible(true);
    },
    [defaultLimitMessage],
  );
  const dispatch = useDispatch();
  const uploadFilesProgressClosed = useSelector(
    (state: any) => state.case.uploadFilesProgressClosed,
  );
  const filesToUpload = useSelector((state: any) => state.case.uploadFiles);

  const uploadFilesPending = useSelector((state: any) => state.case.uploadFilesPending);
  const uploadFilesFeedback = useSelector((state: any) => state.case.uploadFilesFeedback);

  const showProgressLayer =
    !uploadFilesProgressClosed && (uploadFilesPending || filesToUpload.length > 0);
  const isUploading = uploadFilesPending > 0;
  const hasFailures = (filesToUpload || []).some((f: any) => !!f?.error);

  useEffect(() => {
    if (!isUploading) {
      setShowCancelConfirm(false);
    }
  }, [isUploading]);

  // Auto-clear upload UI/state when all uploads finished successfully
  useEffect(() => {
    if (showProgress && !isUploading && !hasFailures && (filesToUpload?.length || 0) > 0) {
      dispatch(closeUploadFilesProgress());
      dispatch(dismissUploadFilesFeedback());
    }
  }, [showProgress, isUploading, hasFailures, filesToUpload, dispatch]);

  // Hide upload UI when switching folders (bundle) or URL context changes
  const prevFolderRef = React.useRef(folderId);
  useEffect(() => {
    if (prevFolderRef.current !== folderId) {
      prevFolderRef.current = folderId;
      // Clear any lingering upload UI/state when folder changes
      dispatch(closeUploadFilesProgress());
      dispatch(dismissUploadFilesFeedback());
    }
  }, [folderId, dispatch]);

  // Build inline upload panel to render in the table empty state while uploading or when failures exist
  const activeFileName = useMemo(() => {
    if (!filesToUpload || filesToUpload.length === 0) return '';
    const inProgress = filesToUpload.find(
      (f: any) => !f?.error && parseFloat(f?.progress || '0') < 100,
    );
    return (inProgress && inProgress.name) || filesToUpload[filesToUpload.length - 1]?.name || '';
  }, [filesToUpload]);

  const uploadHeaderNote = useMemo(() => {
    if (isUploading) {
      return activeFileName
        ? `${T.translate('generic.fileUploadInProgress')}: ${activeFileName}`
        : T.translate('generic.fileUploadInProgress');
    }
    if (hasFailures) {
      return T.translate('generic.fileUploadHasBeenCompleted', {
        status: T.translate('generic.unsuccessful'),
      });
    }
    return '';
  }, [activeFileName, isUploading, hasFailures]);

  const UploadInlinePanel = (
    <Box
      p={2}
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      sx={{ width: '100%', height: '100%', minHeight: 0 }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: '100%', mb: 1 }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {isUploading && <CircularProgress size={20} />}
          {!!uploadHeaderNote && (
            <Typography variant="subtitle2" color="text.secondary">
              {uploadHeaderNote}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={() => {
            // Only show confirmation if there are files actively uploading
            // Files with errors, duplicates, oversized, or completed should not require confirmation
            const hasActiveUploads = filesToUpload && filesToUpload.some((f: any) => {
              const progress = parseFloat(f?.progress || '0');
              const isError = !!f?.error;
              return !isError && progress < 100;
            });
            
            if (uploadFilesPending > 0 || hasActiveUploads) {
              setShowCancelConfirm(true);
              return;
            }
            // Hide the panel and clear state when safe (no active uploads)
            dispatch(closeUploadFilesProgress());
            dispatch(dismissUploadFilesFeedback());
          }}
          aria-label="close upload panel"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', width: '100%', px: 1, minHeight: 0 }}>
        {filesToUpload && filesToUpload.length > 0 ? (
          filesToUpload.map((f: any, idx: number) => {
            const progress = parseFloat(f?.progress || '0');
            const isError = !!f?.error;
            const errMsg = isError ? processError(f.error) : '';
            const isDuplicate =
              isError && (/this file is a duplicate of/i.test(errMsg) || f?.error?.status === 409);
            const isOversized =
              isError && (f?.error?.status === 413 || /file.*exceeds.*limit/i.test(errMsg));
            const pct = Math.min(Math.max(progress, 0), 100);
            return (
              <Box key={`${f.hash || f.name}-${idx}`} sx={{ mb: 1.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{
                      pr: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {f.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isError
                        ? isDuplicate || isOversized
                          ? 'warning.main'
                          : 'error.main'
                        : 'text.secondary',
                    }}
                  >
                    {isError ? errMsg : `${pct.toFixed(0)}%`}
                  </Typography>
                </Box>
                <LinearProgress
                  variant={'determinate'}
                  value={isError ? 100 : pct}
                  color={isError && !isDuplicate && !isOversized ? 'error' : 'primary'}
                  sx={{
                    height: 6,
                    borderRadius: 6,
                    mt: 0.5,
                    ...((isDuplicate || isOversized) && {
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: (theme) => (theme as any).palette.warning.main,
                      },
                      backgroundColor: (theme) => (theme as any).palette.warning.light,
                    }),
                  }}
                />
              </Box>
            );
          })
        ) : (
          <Typography variant="caption" color="text.secondary">
            {T.translate('generic.noUploads') || 'No uploads yet'}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const [{ canDrop /* , isOver */ } /*, drop */] = useDrop(() => ({
    accept: [NativeTypes.FILE],
    // drop: () => ({ name: 'Dustbin' }),
    collect: (monitor: any) => ({
      // isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }));

  const showModal = (location: any) => {
    setModalVisible(true);
    setLastLocation(location);
  };

  const hideModal = () => setModalVisible(false);

  const handleBlockedRoute = (nextLocation: any) => {
    showModal(nextLocation);
    return false;
  };

  const handleConfirmNavigationClick = () => {
    dispatch(closeUploadFilesProgress());
    hideModal();
    history.push(lastLocation.pathname);
  };

  // Generate a stable batchId that persists across renders
  const [batchId] = useState(() => uuidv4());

  const content =
    noDrop || showSidebar ? (
      children
    ) : (
      <Dropzone
        ref={refLink}
        noClick={true}
        onDrop={(acceptedFiles) => {
          const validation = validateFiles(acceptedFiles);
          if (!validation.isValid) {
            showCustomAlert(validation.errorMessage);
            return;
          }

          const dropContext = { batchId, uploadInnerFile, folderId };
          if (onDropCustom) {
            onDropCustom(acceptedFiles, dropContext);
          } else {
            if (uploadInnerFile) {
              dispatch(uploadFiles(acceptedFiles, folderId, uploadInnerFile, batchId));
              setUploadInnerFile(false);
            } else {
              dispatch(uploadFiles(acceptedFiles, folderId, undefined, batchId));
            }
          }
        }}
        onDragEnter={onDragEnter}
      >
        {({ getRootProps, getInputProps, isDragActive }) => {
          return (
            <React.Fragment>
              {(() => {
                // Inject inline upload UI into table when uploading
                if (
                  showProgress &&
                  (isUploading || hasFailures || showProgressLayer) &&
                  React.isValidElement(children)
                ) {
                  return React.cloneElement(children as any, {
                    suppressRows: true,
                    uploadEmptyState: UploadInlinePanel,
                  });
                }
                return children;
              })()}
              {!(isUploading || hasFailures) && (
                <section
                  style={{ pointerEvents: !canDrop ? 'none' : 'all' }}
                  className="case-file-drop-zone"
                >
                  <div
                    className={classnames('drag-layer', {
                      drag: canDrop,
                      'drag-over': isDragActive,
                    })}
                    {...getRootProps()}
                  >
                    {!noInput &&
                      (() => {
                        const { ...restInputProps } = getInputProps();
                        return (
                          <input
                            {...restInputProps}
                            onChange={(event) => {
                              const files = event.target.files
                                ? Array.from(event.target.files)
                                : [];
                              const validation = validateFiles(files);
                              if (!validation.isValid) {
                                showCustomAlert(validation.errorMessage);
                                return;
                              }

                              const dropContext = { batchId, uploadInnerFile, folderId };
                              if (files.length > 0) {
                                if (onDropCustom) {
                                  onDropCustom(files, dropContext);
                                } else {
                                  if (uploadInnerFile) {
                                    dispatch(
                                      uploadFiles(files, folderId, uploadInnerFile, batchId),
                                    );
                                    setUploadInnerFile(false);
                                  } else {
                                    dispatch(uploadFiles(files, folderId, undefined, batchId));
                                  }
                                }
                              }
                            }}
                          />
                        );
                      })()}
                    <MuiModal
                      open={alertVisible}
                      onClose={() => setAlertVisible(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        paddingTop: '5%',
                      }}
                      hideBackdrop
                    >
                      <div
                        style={{
                          backgroundColor: 'white',
                          padding: '20px',
                          borderRadius: '12px',
                          border: '1px solid rgba(68, 68, 68, 0.2)',
                          outline: 'none',
                          maxWidth: '400px',
                        }}
                      >
                        <h3>{T.translate('generic.uploadError')}</h3>
                        <p>{alertMessage}</p>
                        <MuiButton
                          sx={{ float: 'right' }}
                          variant="contained"
                          onClick={() => setAlertVisible(false)}
                        >
                          {T.translate('generic.understood')}
                        </MuiButton>
                      </div>
                    </MuiModal>
                    {showInfo && canDrop ? (
                      <div
                        className={classnames(
                          confidentialityRing ? 'border-box-modal' : 'border-box',
                        )}
                      >
                        <div>
                          <p>
                            <FontAwesomeIcon icon={faCloudUploadAlt} />
                            {confidentialityRing
                              ? T.translate('case.dropHereRingFiles', {
                                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                  //@ts-ignore
                                  ring: confidentialityRingCategoryLabel[confidentialityRing],
                                })
                              : T.translate('case.dropHere')}
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </section>
              )}
            </React.Fragment>
          );
        }}
      </Dropzone>
    );

  return (
    <>
      {/* Keep navigation guard active during active uploads */}
      {showProgress && isUploading && <Prompt when={!modalVisible} message={handleBlockedRoute} />}
      {content}
      <Modal
        show={showCancelConfirm}
        title={T.translate('generic.cancelUploadsTitle')}
        handleClose={() => setShowCancelConfirm(false)}
        handleSave={() => {
          setShowCancelConfirm(false);
          dispatch(cancelUploadFiles());
        }}
        saveTitle={T.translate('generic.cancelUploadsConfirm')}
        closeTitle={T.translate('generic.cancelUploadsKeep')}
      >
        <div>{T.translate('generic.cancelUploadsDescription')}</div>
      </Modal>
      {showProgress && isUploading && (
        <Modal
          show={modalVisible}
          title={T.translate('generic.areYouSure')}
          handleClose={hideModal}
          saveTitle={T.translate('generic.yes')}
          handleSave={handleConfirmNavigationClick}
        >
          <div>{T.translate('generic.reviewFilesUploadedMessage')}</div>
        </Modal>
      )}
    </>
  );
};
